import React, { useMemo } from "react";

const getDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  return R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
};

const getStopPositions = (stops) => {
  if (!stops || stops.length === 0) return [];
  if (stops.length === 1) return [0];

  const cumulative = [0];
  for (let i = 1; i < stops.length; i++) {
    const prev = stops[i - 1];
    const cur = stops[i];
    if (
      !Number.isFinite(prev.lat) ||
      !Number.isFinite(prev.lng) ||
      !Number.isFinite(cur.lat) ||
      !Number.isFinite(cur.lng)
    ) {
      cumulative.push(cumulative[i - 1]);
      continue;
    }
    const dist = getDistance(prev.lat, prev.lng, cur.lat, cur.lng);
    cumulative.push(cumulative[i - 1] + (Number.isFinite(dist) ? dist : 0));
  }

  const totalDist = cumulative[cumulative.length - 1];
  if (!totalDist) {
    return stops.map((_, i) => (i / (stops.length - 1)) * 100);
  }

  return cumulative.map((d) => (d / totalDist) * 100);
};

const getBusProgress = (stops, currentLocation) => {
  if (!stops || stops.length < 2 || !currentLocation) return 0;

  const curLat = currentLocation?.lat;
  const curLng = currentLocation?.lng;
  if (!Number.isFinite(curLat) || !Number.isFinite(curLng)) return 0;

  let totalDist = 0;
  for (let i = 0; i < stops.length - 1; i++) {
    const a = stops[i];
    const b = stops[i + 1];
    if (
      !Number.isFinite(a.lat) ||
      !Number.isFinite(a.lng) ||
      !Number.isFinite(b.lat) ||
      !Number.isFinite(b.lng)
    ) {
      continue;
    }
    totalDist += getDistance(a.lat, a.lng, b.lat, b.lng);
  }

  if (!totalDist) return 0;

  let coveredDist = 0;

  for (let i = 0; i < stops.length - 1; i++) {
    const a = stops[i];
    const b = stops[i + 1];
    if (
      !Number.isFinite(a.lat) ||
      !Number.isFinite(a.lng) ||
      !Number.isFinite(b.lat) ||
      !Number.isFinite(b.lng)
    ) {
      continue;
    }

    const segDist = getDistance(a.lat, a.lng, b.lat, b.lng);
    const distFromThisStop = getDistance(curLat, curLng, a.lat, a.lng);
    const distToNextStop = getDistance(curLat, curLng, b.lat, b.lng);

    // Bus is between stop i and stop i+1 (triangle inequality tolerance)
    if (distFromThisStop + distToNextStop <= segDist + 0.15) {
      coveredDist += distFromThisStop;
      break;
    } else {
      coveredDist += segDist;
    }
  }

  return Math.min((coveredDist / totalDist) * 100, 100);
};

export default function VerticalRouteProgress({ stops = [], currentLocation }) {
  const { safeStops, stopPositions, busProgress } = useMemo(() => {
    const safeStops = (stops || []).map((s) => ({
      stop_name: s.stop_name,
      lat: parseFloat(s.latitude),
      lng: parseFloat(s.longitude),
    }));

    const stopPositions = getStopPositions(safeStops);
    const busProgress = getBusProgress(safeStops, currentLocation);

    return { safeStops, stopPositions, busProgress };
  }, [stops, currentLocation]);

  if (!stops || stops.length === 0) {
    return (
      <div
        style={{
          width: "100%",
          height: "350px",
          padding: "20px",
          border: "1px solid #ddd",
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#666",
          fontSize: "14px",
        }}
      >
        No stops loaded for this route.
      </div>
    );
  }

  return (
    <div
      style={{
        width: "100%",
        height: "350px",
        padding: "20px",
        border: "1px solid #ddd",
        borderRadius: "8px",
        overflowY: "auto",
        overflowX: "visible",
      }}
    >
      <div
        style={{
          position: "relative",
          minHeight: `${Math.max(400, safeStops.length * 60)}px`,
          height: "100%",
          width: "100%",
          paddingLeft: "110px",
          paddingRight: "110px",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: "50%",
            width: "4px",
            transform: "translateX(-50%)",
            background: "#ccc",
            borderRadius: "2px",
          }}
        />

        {safeStops.map((stop, i) => {
          const top = stopPositions[i] ?? 0;
          const isRight = i % 2 === 1;

          return (
            <div
              key={i}
              style={{
                position: "absolute",
                top: `${top}%`,
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}
            >
              <div
                style={{
                  width: "12px",
                  height: "12px",
                  background: "#8B5A2B",
                  borderRadius: "50%",
                  border: "2px solid #fff",
                  boxShadow: "0 0 0 1px rgba(0,0,0,0.15)",
                }}
              />

              <div
                style={{
                  fontSize: "13px",
                  whiteSpace: "nowrap",
                  maxWidth: "180px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  position: "absolute",
                  top: "50%",
                  transform: "translateY(-50%)",
                  left: isRight ? "22px" : "auto",
                  right: isRight ? "auto" : "22px",
                  textAlign: isRight ? "left" : "right",
                  padding: "2px 6px",
                  borderRadius: "6px",
                  background: "rgba(255,255,255,0.85)",
                }}
                title={stop.stop_name}
              >
                {stop.stop_name}
              </div>
            </div>
          );
        })}

        <div
          style={{
            position: "absolute",
            top: `${busProgress}%`,
            left: "calc(50% - 2px)",
            transform: "translateY(-50%)",
            transition: "top 0.3s linear",
            willChange: "top",
            zIndex: 10,
          }}
        >
          🚌
        </div>
      </div>
    </div>
  );
}
